import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, Layers, LogOut, LayoutDashboard, ShieldCheck, Package, Search, BarChart3 } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import { useState } from 'react';

export default function Navbar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/about', label: 'About' },
    { to: '/services', label: 'Services' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 bg-white border-b-2 border-slate-900" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
            <div className="w-9 h-9 bg-[#FF5722] rounded-lg border-2 border-slate-900 flex items-center justify-center shadow-[2px_2px_0px_#0F172A] group-hover:shadow-[3px_3px_0px_#0F172A] transition-all">
              <Layers className="w-5 h-5 text-white" strokeWidth={3} />
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Slice<span className="text-[#FF5722]">Squads</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${
                  isActive(link.to)
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-900 hover:bg-slate-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-3">
            {user && <NotificationBell />}
            {user && (
              <Link to="/orders" data-testid="nav-orders-btn" className="ss-btn ss-btn-outline text-sm !py-2 !px-4 flex items-center gap-2">
                <Package className="w-4 h-4" strokeWidth={2.5} /> Orders
              </Link>
            )}
            {user && user.role === 'admin' && (
              <Link to="/admin" data-testid="nav-admin-btn" className="ss-btn ss-btn-outline text-sm !py-2 !px-4 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" strokeWidth={2.5} /> Admin
              </Link>
            )}
            {user && user.role === 'admin' && (
              <Link to="/admin/seo" data-testid="nav-seo-btn" className="ss-btn ss-btn-outline text-sm !py-2 !px-3 flex items-center gap-1.5" title="SEO Dashboard">
                <Search className="w-4 h-4" strokeWidth={2.5} /> SEO
              </Link>
            )}
            {user && user.role === 'admin' && (
              <Link to="/admin/analytics" data-testid="nav-analytics-btn" className="ss-btn ss-btn-outline text-sm !py-2 !px-3 flex items-center gap-1.5" title="Analytics">
                <BarChart3 className="w-4 h-4" strokeWidth={2.5} /> Analytics
              </Link>
            )}
            {user ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard-btn" className="ss-btn ss-btn-dark text-sm !py-2 !px-4 flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" strokeWidth={2.5} /> Dashboard
                </Link>
                <button onClick={logout} data-testid="nav-logout-btn" className="ss-btn ss-btn-outline text-sm !py-2 !px-4 flex items-center gap-2">
                  <LogOut className="w-4 h-4" strokeWidth={2.5} /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="ss-btn ss-btn-outline text-sm !py-2 !px-4">
                  Log In
                </Link>
                <Link to="/quote" data-testid="nav-get-quote-btn" className="ss-btn ss-btn-primary text-sm !py-2 !px-4">
                  Get a Quote
                </Link>
              </>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 border-2 border-slate-900 rounded-lg"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" strokeWidth={3} /> : <Menu className="w-5 h-5" strokeWidth={3} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden border-t-2 border-slate-900 py-4 space-y-2" data-testid="nav-mobile-menu">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileOpen(false)}
                className={`block px-4 py-2 text-sm font-bold rounded-lg ${
                  isActive(link.to) ? 'bg-slate-900 text-white' : 'text-slate-900 hover:bg-slate-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-slate-200 space-y-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block ss-btn ss-btn-dark text-sm !py-2 text-center">Dashboard</Link>
                  {user.role === 'admin' && (
                    <Link to="/admin" onClick={() => setMobileOpen(false)} className="block ss-btn ss-btn-outline text-sm !py-2 text-center">Admin Panel</Link>
                  )}
                  <button onClick={() => { logout(); setMobileOpen(false); }} className="w-full ss-btn ss-btn-outline text-sm !py-2">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="block ss-btn ss-btn-outline text-sm !py-2 text-center">Log In</Link>
                  <Link to="/quote" onClick={() => setMobileOpen(false)} className="block ss-btn ss-btn-primary text-sm !py-2 text-center">Get a Quote</Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
